import React from 'react';

import { useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';

import { Tooltip } from 'antd';
import classNames from 'classnames';

import BoxContainer from '@src/components/BoxContainer';
import MemberUser from '@src/components/MemberList/MemberUser';
import { ResponsiveTable } from '@src/components/Table';
import { ACTIONS_LAYOUT, RESPONSIVE_COLUMN_TYPE } from '@src/components/Table/constants';
import { getRouteEditUser } from '@src/packages/users/constants';
import { getUserListStatus } from '@src/packages/users/screens/UsersList/utils/getUserListStatus';
import { userHasAttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';
import { getFullUrl } from '@src/packages/utils/functions';
import { JOURNEY_ASSIGNED_USERS_TYPE } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import styles from './styles.module.less';

interface ISelectCompanyMembersProps {
  loading?: boolean;
  onAdd: (user: any) => void;
  data: any[];
  type?: typeof JOURNEY_ASSIGNED_USERS_TYPE.MEMBER | typeof JOURNEY_ASSIGNED_USERS_TYPE.SUPERVISOR;
  /**
   * When true, members who already have a work journey can still be added (e.g. document flows).
   * Default false preserves journey-create behaviour that blocks duplicate assignments.
   */
  allowUsersWithExistingJourney?: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  hostClassName?: string;
  contentClassName?: string;
  tableWrapperClassName?: string;
  tableClassName?: string;
}

const SelectCompanyMembers = ({
  loading,
  onAdd,
  data = [],
  type = JOURNEY_ASSIGNED_USERS_TYPE.MEMBER,
  allowUsersWithExistingJourney = false,
  maxHeight = 'none',
  stickyHeader = false,
  hostClassName,
  contentClassName,
  tableWrapperClassName,
  tableClassName,
}: ISelectCompanyMembersProps) => {
  const { formatMessage } = useIntl();
  const { url } = useRouteMatch();

  const COLUMNS = [
    {
      title: <IntlMessages id="users.select.table.user" />,
      dataIndex: 'displayName',
      key: 'displayName',
      align: 'left',
      render: values => {
        return (
          <MemberUser
            className={styles.memeberSelectUser}
            user={values.user}
            displayName={values.displayName}
          />
        );
      },
    },
    {
      key: 'email',
      title: <IntlMessages id="users.select.table.email" />,
      dataIndex: 'email',
      align: 'left',
      render: values => <span className={styles.cutText}>{values.email}</span>,
    },
    {
      title: <IntlMessages id="button.add" />,
      dataIndex: 'add',
      key: 'add',
      columnType: RESPONSIVE_COLUMN_TYPE.ACTIONS,
      actionsLayout: ACTIONS_LAYOUT.END,
      render: values => {
        const info = values?.user?.info || null;
        const journeyConfig = info?.workJourneyConfigId || null;
        const isSupervisor = type === JOURNEY_ASSIGNED_USERS_TYPE.SUPERVISOR;
        const hasAttendanceAddress = userHasAttendanceAddress(values?.user?.address);
        const assignmentStatus = getUserListStatus(values?.user ?? {});
        const isEligibleStatus = assignmentStatus === 'active';
        const canAddMember =
          isEligibleStatus &&
          (isSupervisor || !journeyConfig || allowUsersWithExistingJourney) &&
          (isSupervisor || hasAttendanceAddress);
        const missingAddressOnly = isEligibleStatus && !isSupervisor && !hasAttendanceAddress;
        const tooltipText =
          assignmentStatus === 'pending'
            ? formatMessage({ id: 'users.select.tooltip.pendingUser' })
            : assignmentStatus === 'inactive'
              ? formatMessage({ id: 'users.select.tooltip.inactiveUser' })
              : missingAddressOnly
                ? formatMessage({ id: 'users.select.tooltip.missingAddress.edit' })
                : journeyConfig && !canAddMember
                  ? formatMessage({ id: 'users.select.tooltip.alreadyConfigured' })
                  : null;

        if (missingAddressOnly) {
          const userId = values?.user?.id || values?.id;
          if (!userId) {
            return null;
          }
          const editPath = getFullUrl(getRouteEditUser(userId), url);
          return (
            <Tooltip title={tooltipText} placement="left">
              <a
                href={editPath}
                target="_blank"
                rel="noopener noreferrer"
                className={classNames(
                  'gx-d-flex gx-justify-content-sm-center gx-pointer',
                  styles.editAddressButton,
                )}
                aria-label={formatMessage({ id: 'users.select.action.editAddress' })}
              >
                <span className={`${styles.addButton} material-symbols-outlined`}>edit</span>
              </a>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={tooltipText} placement="left">
            <div
              onClick={() => canAddMember && onAdd(values)}
              className="gx-d-flex gx-justify-content-sm-center gx-pointer"
            >
              <span
                className={`${styles.addButton} material-symbols-outlined ${!canAddMember ? styles.disabledButton : ''}`}
              >
                add_circle
              </span>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <BoxContainer
      className={classNames(
        stickyHeader ? styles.stickyTableHost : 'gx-overflow-auto',
        hostClassName,
      )}
      contentClassName={classNames(stickyHeader && styles.stickyTableHostInner, contentClassName)}
      empty={data.length === 0}
    >
      <ResponsiveTable
        className={classNames(styles.selectCompanyMembersTable, tableClassName)}
        wrapperClassName={tableWrapperClassName}
        loading={loading}
        dataSource={data}
        columns={COLUMNS}
        maxHeight={maxHeight}
        stickyHeader={stickyHeader}
      />
    </BoxContainer>
  );
};

export default React.memo(SelectCompanyMembers) as React.FC<ISelectCompanyMembersProps>;
